import { DollarSign } from 'lucide-react';

const FG = '#F5F1FA';
const MUTED = '#A99FB8';
const SURFACE2 = '#140C20';
const SURFACE = '#1B1029';
const VIOLET_LIGHT = '#A855F7';
const CHAMPAGNE = '#E8B96A';
const BORDER = 'rgba(255,255,255,0.08)';

const GREEN = '#4ADE80';
const RED = '#F87171';

const STAT_CARDS = [
  {
    label: 'Saldo atual',
    value: 'R$ 84.500,00',
    color: FG,
    badge: null,
  },
  {
    label: 'Receita prevista',
    value: 'R$ 33.000,00',
    color: GREEN,
    badge: 'entrada',
  },
  {
    label: 'Despesa prevista',
    value: 'R$ 11.500,00',
    color: RED,
    badge: 'saída',
  },
  {
    label: 'Pagamentos atrasados',
    value: 'R$ 9.200,00',
    color: RED,
    badge: 'atrasado',
  },
];

// Pares receita/despesa para 6 meses: Jan–Jun
const GRAFICO_MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun'];
const GRAFICO_REC = [28, 32, 25, 36, 30, 33];
const GRAFICO_DESP = [12, 14, 10, 15, 11, 11];
const MAX_VAL = 40;

const LANCAMENTOS = [
  {
    descricao: 'Honorários, Parcela 3/12',
    cliente: 'M. Andrade',
    categoria: 'Honorários',
    valor: '+R$ 1.750,00',
    cor: GREEN,
    acao: 'Receber',
  },
  {
    descricao: 'Aluguel do escritório',
    cliente: null,
    categoria: 'Despesa fixa',
    valor: '-R$ 4.500,00',
    cor: RED,
    acao: 'Pago',
  },
];

export function FinanceiroMockup() {
  return (
    <div
      style={{
        width: '100%',
        borderRadius: 16,
        overflow: 'hidden',
        border: `1px solid ${BORDER}`,
        background: SURFACE2,
        boxShadow:
          '0 0 0 1px rgba(109,40,217,0.15), 0 32px 80px rgba(0,0,0,0.55), 0 8px 24px rgba(109,40,217,0.12)',
        fontFamily: "'Hanken Grotesk', sans-serif",
      }}
    >
      {/* Titlebar */}
      <div
        style={{
          background: 'rgba(20,12,32,0.98)',
          borderBottom: `1px solid rgba(255,255,255,0.07)`,
          padding: '12px 18px',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
        }}
      >
        <div style={{ display: 'flex', gap: 7, flexShrink: 0 }}>
          {['#FF5F56', '#FFBD2E', '#27C93F'].map((c) => (
            <span
              key={c}
              style={{
                width: 11,
                height: 11,
                borderRadius: '50%',
                background: c,
                opacity: 0.75,
                display: 'inline-block',
              }}
            />
          ))}
        </div>
        <DollarSign size={13} color={GREEN} strokeWidth={2} />
        <span style={{ fontSize: 12, fontWeight: 600, color: FG }}>Financeiro</span>
      </div>

      <style>{`
        @media (max-width: 600px) {
          .fin-stat-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .fin-table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }
          .fin-table-inner { min-width: 400px; }
        }
      `}</style>
      <div style={{ padding: '14px 14px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Stat cards */}
        <div className="fin-stat-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
          {STAT_CARDS.map((card, i) => (
            <div
              key={i}
              style={{
                background: SURFACE,
                border: `1px solid ${BORDER}`,
                borderRadius: 10,
                padding: '10px 11px',
              }}
            >
              <p style={{ fontSize: 9, color: MUTED, margin: '0 0 6px', fontWeight: 500 }}>
                {card.label}
              </p>
              <p
                style={{
                  fontFamily: "'Fraunces', Georgia, serif",
                  fontSize: i === 0 ? 14 : 13,
                  fontWeight: 400,
                  color: card.color,
                  margin: 0,
                  lineHeight: 1.2,
                  letterSpacing: '-0.01em',
                }}
              >
                {card.value}
              </p>
              {card.badge && (
                <span
                  style={{
                    display: 'inline-block',
                    marginTop: 5,
                    fontSize: 8,
                    fontWeight: 700,
                    letterSpacing: '0.07em',
                    textTransform: 'uppercase' as const,
                    color: card.color,
                    background:
                      card.badge === 'entrada'
                        ? 'rgba(74,222,128,0.1)'
                        : 'rgba(248,113,113,0.1)',
                    border: `1px solid ${card.badge === 'entrada' ? 'rgba(74,222,128,0.25)' : 'rgba(248,113,113,0.25)'}`,
                    borderRadius: 4,
                    padding: '1px 5px',
                  }}
                >
                  {card.badge}
                </span>
              )}
            </div>
          ))}
        </div>

        {/* Mini grafico de barras */}
        <div
          style={{
            background: SURFACE,
            border: `1px solid ${BORDER}`,
            borderRadius: 10,
            padding: '11px 14px 10px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <span
              style={{
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase' as const,
                color: MUTED,
              }}
            >
              Receita vs Despesa
            </span>
            {/* Legenda */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {[{ label: 'Receita', color: GREEN }, { label: 'Despesa', color: RED }].map((leg) => (
                <div key={leg.label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 2,
                      background: leg.color,
                      opacity: 0.8,
                      display: 'inline-block',
                    }}
                  />
                  <span style={{ fontSize: 9, color: MUTED }}>{leg.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Barras */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${GRAFICO_MESES.length}, 1fr)`,
              gap: 6,
              alignItems: 'flex-end',
              height: 56,
            }}
          >
            {GRAFICO_MESES.map((mes, i) => (
              <div
                key={mes}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                  height: '100%',
                  justifyContent: 'flex-end',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    gap: 2,
                    flex: 1,
                    justifyContent: 'center',
                    width: '100%',
                  }}
                >
                  <div
                    style={{
                      width: 8,
                      height: `${(GRAFICO_REC[i] / MAX_VAL) * 100}%`,
                      background: GREEN,
                      borderRadius: '2px 2px 0 0',
                      opacity: i === GRAFICO_MESES.length - 1 ? 1 : 0.55,
                    }}
                  />
                  <div
                    style={{
                      width: 8,
                      height: `${(GRAFICO_DESP[i] / MAX_VAL) * 100}%`,
                      background: RED,
                      borderRadius: '2px 2px 0 0',
                      opacity: i === GRAFICO_MESES.length - 1 ? 1 : 0.55,
                    }}
                  />
                </div>
                <span
                  style={{
                    fontSize: 8,
                    color: i === GRAFICO_MESES.length - 1 ? FG : MUTED,
                    fontWeight: i === GRAFICO_MESES.length - 1 ? 600 : 400,
                  }}
                >
                  {mes}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Mini tabela de lancamentos */}
        <div
          className="fin-table-wrap"
          style={{
            background: SURFACE,
            border: `1px solid ${BORDER}`,
            borderRadius: 10,
            overflowY: 'hidden',
          }}
        >
          <div className="fin-table-inner">
          {/* Cabecalho */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 90px 90px 80px 60px',
              padding: '7px 12px',
              borderBottom: `1px solid ${BORDER}`,
              background: 'rgba(20,12,32,0.5)',
            }}
          >
            {['Descrição', 'Cliente', 'Categoria', 'Valor', 'Ação'].map((col) => (
              <span
                key={col}
                style={{
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase' as const,
                  color: MUTED,
                }}
              >
                {col}
              </span>
            ))}
          </div>

          {/* Linhas */}
          {LANCAMENTOS.map((l, i) => (
            <div
              key={i}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 90px 90px 80px 60px',
                padding: '9px 12px',
                borderBottom: i < LANCAMENTOS.length - 1 ? `1px solid ${BORDER}` : 'none',
                alignItems: 'center',
              }}
            >
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 500,
                  color: FG,
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                }}
              >
                {l.descricao}
              </span>

              {/* Cliente chip ou vazio */}
              <div>
                {l.cliente ? (
                  <span
                    style={{
                      fontSize: 9,
                      fontWeight: 600,
                      color: VIOLET_LIGHT,
                      background: 'rgba(168,85,247,0.1)',
                      border: '1px solid rgba(168,85,247,0.2)',
                      borderRadius: 4,
                      padding: '2px 6px',
                    }}
                  >
                    {l.cliente}
                  </span>
                ) : (
                  <span style={{ fontSize: 9, color: 'rgba(169,159,184,0.3)' }}>—</span>
                )}
              </div>

              <span style={{ fontSize: 10, color: MUTED }}>{l.categoria}</span>

              <span style={{ fontSize: 11, fontWeight: 700, color: l.cor }}>{l.valor}</span>

              <button
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  fontSize: 9,
                  fontWeight: 700,
                  color: l.cor,
                  background:
                    l.cor === GREEN ? 'rgba(74,222,128,0.08)' : 'rgba(248,113,113,0.08)',
                  border: `1px solid ${l.cor === GREEN ? 'rgba(74,222,128,0.2)' : 'rgba(248,113,113,0.2)'}`,
                  borderRadius: 5,
                  padding: '3px 8px',
                  cursor: 'default',
                  fontFamily: 'inherit',
                }}
              >
                {l.acao}
              </button>
            </div>
          ))}
        </div>
          </div>{/* /fin-table-inner */}
      </div>
    </div>
  );
}
